import { defineStore } from 'pinia'
import { defaultState, getLocalState, setLocalState } from './helper'
import { router } from '@/router'

export const useChatStore = defineStore('chat-store', {
  state: (): Chat.ChatState => getLocalState(),

  getters: {
    getChatHistoryByCurrentActive(state: Chat.ChatState) {
      const index = state.history.findIndex(item => item.sessionId === state.active)
      if (index !== -1) {
        return state.history[index]
      }
      return null
    },

    getChatBysessionId(state: Chat.ChatState) {
      return (sessionId?: string) => {
        if (sessionId) {
          return state.chat.find(item => item.sessionId === sessionId)?.data ?? []
        }
        return state.chat.find(item => item.sessionId === state.active)?.data ?? []
      }
    },
  },

  actions: {
    updateRecommendList(list: Chat.Relevant[]) {
      this.recommendList = list
      this.recordState()
    },
    updateCurrentSessionId(sessionId: string) {
      this.currentSessionId = sessionId
      this.recordState()
    },
    updateCurrentScene(defaultScene: Chat.Scene | null) {
      this.currentScene = defaultScene
      this.recordState()
    },
    updateChatSceneList(sceneList: Chat.Scene[]) {
      this.sceneList = sceneList
      this.recordState()
    },
    setUsingContext(context: boolean) {
      this.usingContext = context
      this.recordState()
    },

    addHistory(history: Chat.History, chatData: Chat.Chat[] = []) {
      this.history.unshift(history)
      this.chat.unshift({ sessionId: history.sessionId, data: chatData })
      this.active = history.sessionId
      this.recordState()
      // this.reloadRoute(history.sessionId)
    },
    updateHistorySync(list: Chat.History[]) {
      this.history = list
    },
    updateHistory(sessionId: string, edit: Partial<Chat.History>) {
      const index = this.history.findIndex(item => item.sessionId === sessionId)
      if (index !== -1) {
        this.history[index] = { ...this.history[index], ...edit }
        this.recordState()
      }
    },

    async deleteHistory(index: number) {
      this.history.splice(index, 1)
      this.chat.splice(index, 1)

      if (this.history.length === 0) {
        this.active = null
        this.reloadRoute()
        return
      }

      if (index > 0 && index <= this.history.length) {
        const sessionId = this.history[index - 1].sessionId
        this.active = sessionId
        this.reloadRoute(sessionId)
        return
      }

      if (index === 0) {
        if (this.history.length > 0) {
          const sessionId = this.history[0].sessionId
          this.active = sessionId
          this.reloadRoute(sessionId)
        }
      }

      if (index > this.history.length) {
        const sessionId = this.history[this.history.length - 1].sessionId
        this.active = sessionId
        this.reloadRoute(sessionId)
      }
    },

    async setActive(sessionId: string) {
      this.active = sessionId
      return await this.reloadRoute(sessionId)
    },

    getChatBysessionIdAndIndex(sessionId: string, index: number) {
      if (!sessionId) {
        if (this.chat.length) {
          return this.chat[0].data[index]
        }
        return null
      }
      const chatIndex = this.chat.findIndex(item => item.sessionId === sessionId)
      if (chatIndex !== -1) {
        return this.chat[chatIndex].data[index]
      }
      return null
    },

    addChatBysessionId(sessionId: string, chat: Chat.Chat) {
      if (!sessionId) {
        if (this.history.length === 0) {
          // 默认sessionId
          const sessionId = String(Date.now())
          this.history.push({ sessionId, title: chat.text, isEdit: false })
          this.chat.push({ sessionId, data: [chat] })
          this.active = sessionId
          this.recordState()
        } else {
          this.chat[0].data.push(chat)
          if (this.history[0].title === 'New Chat') {
            this.history[0].title = chat.text
          }
          this.recordState()
        }
      }

      const index = this.chat.findIndex(item => item.sessionId === sessionId)
      if (index !== -1) {
        this.chat[index].data.push(chat)
        if (this.history[index] && this.history[index].title === 'New Chat') {
          this.history[index].title = chat.text
        }
        this.recordState()
      }
    },

    updateChatListBysessionId(sessionId: string, chats: Chat.Chat[]) {
      if (!sessionId) {
        if (this.chat.length) {
          this.chat[0].data = chats
          this.recordState()
        }
        return
      }

      const chatIndex = this.chat.findIndex(item => item.sessionId === sessionId)

      if (chatIndex !== -1) {
        this.chat[chatIndex].data = chats
        this.recordState()
      } else {
        this.chat.push({ sessionId, data: chats })
        this.recordState()
      }
    },

    updateChatBysessionId(sessionId: string, index: number, chat: Chat.Chat) {
      if (!sessionId) {
        if (this.chat.length) {
          this.chat[0].data[index] = chat
          this.recordState()
        }
        return
      }

      const chatIndex = this.chat.findIndex(item => item.sessionId === sessionId)
      if (chatIndex !== -1) {
        this.chat[chatIndex].data[index] = chat
        this.recordState()
      }
    },

    updateChatSomeBysessionId(sessionId: string, index: number, chat: Partial<Chat.Chat>) {
      if (!sessionId) {
        if (this.chat.length) {
          this.chat[0].data[index] = { ...this.chat[0].data[index], ...chat }
          this.recordState()
        }
        return
      }

      const chatIndex = this.chat.findIndex(item => item.sessionId === sessionId)

      if (chatIndex !== -1) {
        this.chat[chatIndex].data[index] = { ...this.chat[chatIndex].data[index], ...chat }
        this.recordState()
      }
    },

    deleteChatBysessionId(sessionId: string, index: number) {
      if (!sessionId) {
        if (this.chat.length) {
          this.chat[0].data.splice(index, 1)
          this.recordState()
        }
        return
      }

      const chatIndex = this.chat.findIndex(item => item.sessionId === sessionId)
      if (chatIndex !== -1) {
        this.chat[chatIndex].data.splice(index, 1)
        this.recordState()
      }
    },

    clearChatBysessionId(sessionId: string) {
      if (!sessionId) {
        if (this.chat.length) {
          this.chat[0].data = []
          this.recordState()
        }
        return
      }

      const index = this.chat.findIndex(item => item.sessionId === sessionId)
      if (index !== -1) {
        this.chat[index].data = []
        this.recordState()
      }
    },

    async reloadRoute(sessionId?: string) {
      this.recordState()
      await router.push({ name: 'Chat', params: { sessionId } })
    },

    recordState() {
      setLocalState(this.$state)
    },

    resetLocalState() {
      console.log('resetLocalState')
      setLocalState(defaultState())
    },
  },
})
